import { FC, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import { Button, Box, Typography, IconButton } from '@mui/material'
import { FFModal } from '~/shared/ui'
import { ShiftException } from './utils'
import ShiftExceptionEditor from './ShiftExceptionEditor'
import { areIntervalsOverlapping } from 'date-fns'
import { haveChanged } from '~/shared/lib/haveChanged'

interface Props {
  shiftExceptions: ShiftException[] | null
  loading: boolean
  isHistory: boolean
  handleCancel: () => void
  ingestionJobId: number | null
  providerId: number
  handleSubmit: (
    shiftExceptionsData: ShiftException[],
    deletedShiftExceptionsData: ShiftException[],
  ) => void
}

const EditShiftExceptionTimingsModal: FC<Props> = (props: Props) => {
  const [tempId, setTempId] = useState<number>(-1)
  const [shiftExceptionsData, setShiftExceptionsData] = useState<ShiftException[]>([])
  const [deletedShiftExceptionsData, setDeletedShiftExceptionsData] = useState<ShiftException[]>([])
  const [error, setError] = useState<boolean[]>([])
  const [overlapErrors, setOverlapErrors] = useState<boolean[]>([])
  const [isEdited, setIsEdited] = useState<boolean>(false)

  // Compute pairwise overlaps
  const computeOverlapErrors = (shiftExceptions: ShiftException[]) => {
    const overlaps = Array<boolean>(shiftExceptions.length).fill(false)

    for (let currentIndex = 0; currentIndex < shiftExceptions.length; currentIndex++) {
      const currentException = shiftExceptions[currentIndex]
      const currentStart = currentException?.period?.lower
        ? new Date(currentException.period.lower)
        : null
      const currentEnd = currentException?.period?.upper
        ? new Date(currentException.period.upper)
        : null

      if (!currentStart || !currentEnd) continue

      for (let otherIndex = currentIndex + 1; otherIndex < shiftExceptions.length; otherIndex++) {
        const otherException = shiftExceptions[otherIndex]
        const otherStart = otherException?.period?.lower
          ? new Date(otherException.period.lower)
          : null
        const otherEnd = otherException?.period?.upper
          ? new Date(otherException.period.upper)
          : null

        if (!otherStart || !otherEnd) continue

        // Endpoint comparison is exclusive: two exceptions that meet at a boundary
        // — one ending exactly when the next begins — are adjacent, not
        // overlapping. Passing `{ inclusive: true }` here would flag them.
        if (
          areIntervalsOverlapping(
            { start: currentStart, end: currentEnd },
            { start: otherStart, end: otherEnd },
          )
        ) {
          overlaps[currentIndex] = true
          overlaps[otherIndex] = true
        }
      }
    }
    return overlaps
  }

  // The editable rows reset to the loaded exceptions whenever those load.
  const loadSource = [props.loading, props.shiftExceptions]
  const [loadedFrom, setLoadedFrom] = useState<unknown[]>()
  if (haveChanged(loadedFrom, loadSource)) {
    setLoadedFrom(loadSource)
    if (!props.loading && props.shiftExceptions) {
      setShiftExceptionsData(props.shiftExceptions)
      setError(props.shiftExceptions.map(_ => false))
      setOverlapErrors(computeOverlapErrors(props.shiftExceptions))
    }
  }

  // Recompute overlaps whenever the local data changes (only if the row level validation has already passed)
  const overlapSource = [shiftExceptionsData, error]
  const [overlapsFrom, setOverlapsFrom] = useState<unknown[]>()
  if (haveChanged(overlapsFrom, overlapSource)) {
    setOverlapsFrom(overlapSource)
    if (!error.some(Boolean)) setOverlapErrors(computeOverlapErrors(shiftExceptionsData))
  }

  const handleUpdateShiftExceptions = (editedShiftException: ShiftException) => {
    const prevShiftException = shiftExceptionsData.find(
      (shiftException: ShiftException) => shiftException.id === editedShiftException.id,
    )
    if (prevShiftException) {
      const prevShiftExceptionIndex = shiftExceptionsData.indexOf(prevShiftException)
      setShiftExceptionsData([
        ...shiftExceptionsData.slice(0, prevShiftExceptionIndex),
        { ...editedShiftException, isEdited: true },
        ...shiftExceptionsData.slice(prevShiftExceptionIndex + 1),
      ])
      setIsEdited(true)
    }
  }

  const handleDeleteShiftExceptions = (deletedShiftException: ShiftException) => {
    const nextData = shiftExceptionsData.filter(
      (shiftException: ShiftException) => shiftException.id !== deletedShiftException.id,
    )
    setShiftExceptionsData(nextData)

    // Track deletions for existing exceptions only (id > 0)
    if (deletedShiftException.id > 0) {
      setDeletedShiftExceptionsData([...deletedShiftExceptionsData, deletedShiftException])
    }

    // Mark the form as edited regardless of id so Update button enables
    setIsEdited(true)

    // Reset errors to match the new items length to avoid stale indices
    setError(() => Array<boolean>(nextData.length).fill(false))

    // Also reset overlapErrors to match the new data length
    setOverlapErrors(() => Array<boolean>(nextData.length).fill(false))
  }

  const handleAddShiftExceptions = () => {
    const shiftException: ShiftException = {
      id: tempId,
      period: { lower: '', upper: '', bounds: '[)' },
      reason: null,
      isEdited: true,
      isAllDay: false, // Initialize isAllDay property for new shift exceptions
    }
    setShiftExceptionsData([...shiftExceptionsData, shiftException])
    setIsEdited(true)
    setTempId((prevTempId: number) => prevTempId - 1)
    setError((prevError: boolean[]) => [...prevError, false])
    setOverlapErrors((prevOverlapErrors: boolean[]) => [...prevOverlapErrors, false])
  }

  const handleUpdate = () => {
    if (!error.some(Boolean) && !overlapErrors.some(Boolean)) {
      props.handleSubmit(shiftExceptionsData, deletedShiftExceptionsData)
      setDeletedShiftExceptionsData([])
      setIsEdited(false)
    }
  }

  const handleCancel = () => {
    setIsEdited(false)
    props.handleCancel()
  }

  if (props.loading || !props.shiftExceptions) return null

  return (
    <FFModal
      open={true}
      style={{ width: '140%', left: '-20%' }}
      header={
        <Box sx={{ p: 1 }}>
          <Typography variant="h6" align="left">
            {props.isHistory ? 'Exceptions and Time Off History' : 'Edit Exceptions and Time Off'}
          </Typography>
        </Box>
      }
      footer={
        <Box sx={{ my: 1, mx: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button color="secondary" disabled={props.loading} onClick={handleCancel}>
            Cancel
          </Button>
          <Box sx={{ ml: 1, display: props.isHistory ? 'none' : 'block' }}>
            <Button
              color="primary"
              type="submit"
              variant="contained"
              disabled={
                props.loading || !isEdited || overlapErrors.some(Boolean) || error.some(Boolean)
              }
              onClick={handleUpdate}
            >
              Update
            </Button>
          </Box>
        </Box>
      }
    >
      <Box sx={{ m: 2 }}>
        {shiftExceptionsData.map((shiftException: ShiftException, i: number) => (
          <ShiftExceptionEditor
            key={shiftException.id}
            shiftException={shiftException}
            isHistory={props.isHistory}
            overlapError={overlapErrors[i]}
            onError={(hasError: boolean) =>
              setError(prevError => {
                const next = [...prevError]
                next[i] = hasError
                return next
              })
            }
            onChange={handleUpdateShiftExceptions}
            onDelete={handleDeleteShiftExceptions}
          />
        ))}
        <IconButton
          color="primary"
          onClick={handleAddShiftExceptions}
          sx={{
            '&:hover': {
              backgroundColor: 'transparent',
            },
            borderRadius: 'unset',
            marginTop: 3,
            display: props.isHistory ? 'none' : 'inline-flex',
          }}
        >
          <AddIcon color="inherit" sx={{ fontSize: '1.8rem', fontWeight: 500 }} />
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 500, ml: 1 }}>
            Add a shift exception
          </Typography>
        </IconButton>
      </Box>
    </FFModal>
  )
}

export default EditShiftExceptionTimingsModal
